import React from "react";
import Page from "@jumbo/shared/Page";
import Auth from "./middleware/Auth";
import Public from "./middleware/Public";
import Admin from "./middleware/Admin";
import Error404 from "app/pages/extra-pages/Error404";
import Login2 from "app/pages/auth-pages/login2";
import Signup2 from "app/pages/auth-pages/signup2";
import ForgotPassword from "app/pages/auth-pages/forgot-password/ForgotPassword";
import ProgrammingContentPage from "app/pages/ProgrammingContent/ProgrammingContentDetail";
import ProgrammingLanguagesPage from "app/pages/ProgrammingContent/ProgrammingLanguagesPage";
import LanguageContentPage from "app/pages/ProgrammingContent/LanguageContentPage";
import dashboardRoutes from "./dashboardRoutes";
import metricsRoutes from "./metricsRoutes";
import widgetsRoutes from "./widgetRoutes";
import calendarRoutes from "./calendarRoutes";
import chartRoutes from "./chartRoutes";
import mapRoutes from "./mapRoutes";
import extensionsRoutes from "./extensionsRoutes";
import extraRoutes from "./extraRoutes";
import appsRoutes from "./appsRoutes";
import { muiRoutes } from "./muiRoutes";
import authRoutes from "./authRoutes";
import userRoutes from "./userRoutes";
import listViewRoutes from "./listViewRoutes";
import gridViewRoutes from "./gridViewRoutes";
import { ROUTES } from "app/utils/constants/routeConstants";
import ProblemListPage from "app/pages/Problems/ProblemList";
import ProblemDetailPage from "app/pages/ProblemDetail/ProblemDetail";
import VerifyAccount from "app/pages/auth-pages/verify-account";
import ActivateAccount from "app/pages/auth-pages/activate-account";
import PricingPlan from "app/pages/Pricing/PricingList";
import PricingDetails from "app/pages/Pricing/PricingDetails";
import ResetForgotPassword from "app/pages/auth-pages/forgot-password/ResetForgotPassword";
import {
  AdminEntityCreateUpdateForm,
  AdminEntityDetails,
  AdminEntityList,
} from "app/pages/admin";
import { ALL_ENTITIES } from "app/utils/constants/entityConstants";
import Report from "app/pages/report/Report";
import HomePage from "app/pages/home";
import AboutUs from "app/pages/extra-pages/about-us";
import CancellationRefundPolicy from "app/pages/extra-pages/cancellation-refund-policy";
import PrivacyPolicy from "../pages/extra-pages/privacy-policy";
import TermsAndConditions from "../pages/extra-pages/terms-and-conditions";
import ContactUs from "../pages/extra-pages/contact-us";

const generateAdminRoutes = (entity) => {
  const defaultComponents = {
    list: (props) => <AdminEntityList entityConfig={entity} {...props} />,
    add: (props) => (
      <AdminEntityCreateUpdateForm entityConfig={entity} {...props} />
    ),
    update: (props) => (
      <AdminEntityCreateUpdateForm entityConfig={entity} {...props} />
    ),
    detail: (props) => <AdminEntityDetails entityConfig={entity} {...props} />,
  };

  return Object.entries(entity.uiPaths).flatMap(([key, config]) => {
    const component = config.component || defaultComponents[key];

    if (key === "list") {
      const detailComponent =
        entity.uiPaths.detail?.component || defaultComponents.detail;
      return [
        { path: config.path, element: <Page component={component} /> },
        {
          path: `${config.path}/:id`,
          element: <Page component={detailComponent} />,
        },
      ];
    } else if (key === "add" || key === "update") {
      return [{ path: config.path, element: <Page component={component} /> }];
    }
    return [];
  });
};

// Generate admin routes for all entities in ALL_ENTITIES
const adminEntityRoutes = ALL_ENTITIES.flatMap((entity) =>
  generateAdminRoutes(entity)
);

const routesForPublic = [
  {
    path: ROUTES.HOME,
    element: <Page component={HomePage} />,
  },
  {
    path: ROUTES.USER_HOME,
    element: <Page component={HomePage} />,
  },
  {
    path: ROUTES.ALGOS_AND_DS,
    element: <Page component={ProblemListPage} />,
  },
  {
    path: ROUTES.ALGOS_AND_DS_DETAIL,
    element: <Page component={ProblemDetailPage} />,
  },
  {
    path: ROUTES.UPGRADE,
    element: <Page component={PricingPlan} />,
  },
  {
    path: ROUTES.UPGRADE + "/:id",
    element: <Page component={PricingDetails} />,
  },
  {
    path: ROUTES.ABOUT_US,
    element: <Page component={AboutUs} />,
  },
  {
    path: ROUTES.CANCELLATION_REFUND_POLICY,
    element: <Page component={CancellationRefundPolicy} />,
  },
  {
    path: ROUTES.PRIVACY_POLICY,
    element: <Page component={PrivacyPolicy} />,
  },
  {
    path: ROUTES.TERMS_AND_CONDITIONS,
    element: <Page component={TermsAndConditions} />,
  },
  {
    path: ROUTES.CONTACT_US,
    element: <Page component={ContactUs} />,
  },
];

/**
 routes only accessible to authenticated users
 **/
const routesForAuthenticatedOnly = [
  {
    middleware: [
      {
        element: Auth,
        fallbackPath: ROUTES.HOME,
      },
    ],
    routes: [
      {
        path: ROUTES.USER_HOME,
        element: <Page component={HomePage} />,
      },
      {
        path: ROUTES.PROGRAMMING_CONTENT,
        element: <Page component={ProgrammingLanguagesPage} />,
      },
      {
        path: ROUTES.PROGRAMMING_CONTENT_LANGUAGE,
        element: <Page component={LanguageContentPage} />,
      },
      {
        path: ROUTES.PROGRAMMING_CONTENT_LANGUAGE_URL,
        element: <Page component={ProgrammingContentPage} />,
      },
      {
        path: ROUTES.REPORT,
        element: <Page component={Report} />,
      },
    ],
  },
];

/**
  routes for admin only
 **/
const routesForAdminOnly = [
  {
    middleware: [
      {
        element: Auth,
        fallbackPath: ROUTES.USER_LOGIN,
      },
      {
        element: Admin,
        fallbackPath: ROUTES.ERROR_404,
        fallbackComponent: <Page component={Error404} />,
      },
    ],
    routes: [
      ...adminEntityRoutes,
      ...dashboardRoutes,
      ...appsRoutes,
      ...widgetsRoutes,
      ...metricsRoutes,
      ...muiRoutes,
      ...extensionsRoutes,
      ...calendarRoutes,
      ...chartRoutes,
      ...mapRoutes,
      ...authRoutes,
      ...extraRoutes,
      ...userRoutes,
      ...listViewRoutes,
      ...gridViewRoutes,
    ],
  },
];

/**
 routes only accessible when user is anonymous
 **/
const routesForNotAuthenticatedOnly = [
  {
    middleware: [
      {
        element: Public,
        fallbackPath: ROUTES.USER_HOME,
      },
    ],
    routes: [
      {
        path: ROUTES.USER_LOGIN,
        element: <Page component={Login2} disableSmLogin={true} />,
      },
      {
        path: ROUTES.USER_SIGNUP,
        element: <Page component={Signup2} disableSmLogin={true} />,
      },
      {
        path: ROUTES.USER_VERIFY_ACCOUNT,
        element: <Page component={VerifyAccount} disableSmLogin={true} />,
      },
      {
        path: ROUTES.USER_CONFIRM_ACCOUNT,
        element: <Page component={Signup2} disableSmLogin={true} />,
      },
      {
        path: ROUTES.USER_FORGOT_PASSWORD,
        element: <Page component={ForgotPassword} disableSmLogin={true} />,
      },
      {
        path: ROUTES.USER_RESET_FORGOT_PASSWORD,
        element: <Page component={ResetForgotPassword} disableSmLogin={true} />,
      },
      {
        path: ROUTES.USER_ACTIVATE,
        element: <Page component={ActivateAccount} disableSmLogin={true} />,
      },
      {
        path: ROUTES.HOME,
        element: <Page component={ProgrammingLanguagesPage} />,
      },
      {
        path: ROUTES.ERROR_404,
        element: (
          <Page
            component={Error404}
            layout={"solo-page"}
            disableSmLogin={true}
          />
        ),
      },
    ],
  },
];

const routes = [
  ...routesForPublic,
  ...routesForAuthenticatedOnly,
  ...routesForNotAuthenticatedOnly,
  ...routesForAdminOnly,
];

export {
  routes as default,
  routesForPublic,
  routesForNotAuthenticatedOnly,
  routesForAuthenticatedOnly,
};
